import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import {
  MagnifyingGlass, Download, WarningCircle, ArrowSquareOut, ChartLineDown, Users, PaperPlaneTilt, EnvelopeSimple,
} from '@phosphor-icons/react';
import {
  getPalletAuditLedger, getPalletCounterpartyOutstanding, getPalletUnreturnedOutbounds,
  palletAuditCsvUrl, sendPalletRecallReminders, getPalletMagicLinkStats,
  getWarehouses, list3plMerchants,
} from '../../lib/api';
import { KIND_LABELS, KIND_TONE, fmtDateTime } from './utils';

const DEFAULT_THRESHOLD = 30;

export default function LossInvestigatorTab({ types }) {
  const location = useLocation();
  const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const pooledFirst = useMemo(
    () => [...types].sort((a, b) => (b.is_pooled ? 1 : 0) - (a.is_pooled ? 1 : 0)),
    [types],
  );
  const [typeId, setTypeId] = useState(urlParams.get('type_id') || '');
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');
  const [thresholdDays, setThresholdDays] = useState(DEFAULT_THRESHOLD);
  const [warehouseId, setWarehouseId] = useState(urlParams.get('warehouse_id') || '');
  const [merchantId, setMerchantId] = useState(urlParams.get('merchant_id') || '');
  const [ledger, setLedger] = useState(null);
  const [counter, setCounter] = useState([]);
  const [unret, setUnret] = useState(null);
  const [warehouses, setWarehouses] = useState({});
  const [merchants, setMerchants] = useState({});
  const [loading, setLoading] = useState(false);
  const [sendingRecall, setSendingRecall] = useState(false);
  const [magicStats, setMagicStats] = useState(null);

  // Magic-link funnel: chases sent → clicks → returns (last 30 days, tenant-wide)
  useEffect(() => {
    getPalletMagicLinkStats({ days: 30 }).then((r) => setMagicStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!typeId && pooledFirst.length) {
      const chep = pooledFirst.find((t) => t.code === 'CHEP') || pooledFirst[0];
      setTypeId(chep?.id || '');
    }
    // Hydrate merchant + warehouse names once for the ledger rows.
    getWarehouses().then((r) => {
      const m = {}; (r.data || []).forEach((w) => { m[w.id] = w.name; });
      setWarehouses(m);
    }).catch(() => {});
    list3plMerchants().then((r) => {
      const m = {}; (r.data || []).forEach((x) => { m[x.id] = x.name; });
      setMerchants(m);
    }).catch(() => {});
  }, [pooledFirst.length]);

  const runInvestigation = async () => {
    if (!typeId) { toast.error('Pick a pallet type first'); return; }
    setLoading(true);
    try {
      const params = { type_id: typeId };
      if (since) params.since = new Date(since).toISOString();
      if (until) params.until = new Date(until).toISOString();
      if (warehouseId) params.warehouse_id = warehouseId;
      if (merchantId) params.merchant_id = merchantId;
      const unretParams = { ...params, threshold_days: Number(thresholdDays) || DEFAULT_THRESHOLD };
      const [l, c, u] = await Promise.all([
        getPalletAuditLedger(params),
        getPalletCounterpartyOutstanding(params),
        getPalletUnreturnedOutbounds(unretParams),
      ]);
      setLedger(l.data);
      setCounter(c.data || []);
      setUnret(u.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Investigation failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (typeId) runInvestigation();
  }, [typeId, warehouseId, merchantId]);

  const csvHref = useMemo(() => {
    if (!typeId) return null;
    const p = { type_id: typeId, threshold_days: thresholdDays };
    if (since) p.since = new Date(since).toISOString();
    if (until) p.until = new Date(until).toISOString();
    return palletAuditCsvUrl(p);
  }, [typeId, since, until, thresholdDays]);

  const sendRecalls = async (force = false) => {
    if (!typeId) return;
    const overCount = unret?.grand_total_over_threshold ?? 0;
    if (overCount === 0) {
      toast.info('No aged unreturned tickets to chase.');
      return;
    }
    if (!window.confirm(
      `Send pallet-recall emails to every merchant with unreturned outbounds aged over ${thresholdDays} days? A 7-day cooldown prevents spam${force ? ' (overridden by force)' : ''}.`,
    )) return;
    setSendingRecall(true);
    try {
      const params = { type_id: typeId, threshold_days: thresholdDays };
      if (since) params.since = new Date(since).toISOString();
      if (until) params.until = new Date(until).toISOString();
      if (force) params.force = true;
      const { data } = await sendPalletRecallReminders(params);
      const bits = [];
      if (data.sent_count) bits.push(`${data.sent_count} sent`);
      if (data.skipped_count) bits.push(`${data.skipped_count} skipped`);
      if (data.error_count) bits.push(`${data.error_count} errored`);
      toast.success(`Recall run · ${bits.join(' · ') || 'no candidates'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Recall send failed');
    } finally {
      setSendingRecall(false);
    }
  };

  // Down-sample ledger rows into an SVG line chart. Renders running_balance
  // over time so drift patterns pop visually. Falls back to a single flat
  // line when there are fewer than 2 points.
  const chart = useMemo(() => {
    const rows = ledger?.rows || [];
    if (rows.length < 2) return null;
    const W = 900; const H = 140; const P = 24;
    const values = rows.map((r) => r.running_balance);
    const minV = Math.min(...values, ledger.opening_balance);
    const maxV = Math.max(...values, ledger.opening_balance);
    const range = Math.max(1, maxV - minV);
    const xStep = (W - 2 * P) / (rows.length - 1);
    const yFor = (v) => H - P - ((v - minV) / range) * (H - 2 * P);
    const path = rows.map((r, i) => `${i === 0 ? 'M' : 'L'} ${(P + i * xStep).toFixed(1)} ${yFor(r.running_balance).toFixed(1)}`).join(' ');
    const zeroY = yFor(ledger.opening_balance);
    const first = rows[0]?.created_at?.slice(0, 10);
    const last = rows[rows.length - 1]?.created_at?.slice(0, 10);
    const driftIdx = ledger.first_drift_row_id
      ? rows.findIndex((r) => r.id === ledger.first_drift_row_id) : -1;
    // Photo/damage markers: any row with a photo attached OR kind in loss/damage
    const eventMarkers = rows.map((r, i) => ({
      row: r, i,
      isLoss: r.kind === 'loss' || r.kind === 'damage',
      hasPhoto: (r.photos_count || 0) > 0,
    })).filter((m) => m.isLoss || m.hasPhoto);
    return { W, H, P, path, zeroY, minV, maxV, first, last, xStep, yFor, driftIdx, rows, eventMarkers };
  }, [ledger]);

  return (
    <div className="space-y-5" data-testid="loss-investigator-tab">
      {/* Header intro */}
      <div className="border border-border bg-muted/30 p-4">
        <div className="flex items-start gap-3">
          <MagnifyingGlass size={22} weight="bold" className="text-brand shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-bold tracking-tight">Pallet Loss Investigator</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Reconstruct the ledger to trace missing pooled pallets. Pick a type + optional
              date window → the tool shows a running balance, outstanding by counterparty, and
              every unreturned outbound ticket. Export the CSV as your CHEP/Loscam dispute pack.
            </p>
            {(warehouseId || merchantId) && (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
                <span className="text-muted-foreground">Pre-filtered from Balances:</span>
                {warehouseId && (
                  <span className="border border-border bg-card px-2 py-0.5 font-mono flex items-center gap-2" data-testid="inv-prefilter-warehouse">
                    Warehouse · {warehouses[warehouseId] || warehouseId.slice(0, 6)}
                    <button className="text-rose-600 hover:underline" onClick={() => setWarehouseId('')}>×</button>
                  </span>
                )}
                {merchantId && (
                  <span className="border border-border bg-card px-2 py-0.5 font-mono flex items-center gap-2" data-testid="inv-prefilter-merchant">
                    Merchant · {merchants[merchantId] || merchantId.slice(0, 6)}
                    <button className="text-rose-600 hover:underline" onClick={() => setMerchantId('')}>×</button>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 border border-border bg-card p-3">
        <div>
          <Label>Pallet type</Label>
          <select
            data-testid="investigator-type"
            className="w-full border border-border bg-background h-9 px-2 text-sm"
            value={typeId} onChange={(e) => setTypeId(e.target.value)}
          >
            <option value="">Select…</option>
            {pooledFirst.map((t) => (
              <option key={t.id} value={t.id}>{t.code} · {t.name}{t.is_pooled ? ' (pooled)' : ''}</option>
            ))}
          </select>
        </div>
        <div>
          <Label>From</Label>
          <Input type="date" value={since} onChange={(e) => setSince(e.target.value)} data-testid="investigator-since" />
        </div>
        <div>
          <Label>To</Label>
          <Input type="date" value={until} onChange={(e) => setUntil(e.target.value)} data-testid="investigator-until" />
        </div>
        <div>
          <Label>Ageing threshold (days)</Label>
          <Input type="number" min="1" max="365" value={thresholdDays}
            onChange={(e) => setThresholdDays(e.target.value)} data-testid="investigator-threshold" />
        </div>
        <div className="flex items-end gap-2">
          <Button className="w-full" onClick={runInvestigation} disabled={loading || !typeId} data-testid="investigator-run">
            {loading ? 'Running…' : 'Investigate'}
          </Button>
          <Button
            variant="outline"
            onClick={() => sendRecalls(false)}
            disabled={sendingRecall || !unret || unret.grand_total_over_threshold === 0}
            data-testid="investigator-send-recalls"
            title="Email every merchant with aged unreturned outbounds. 7-day cooldown prevents spam."
            className="whitespace-nowrap"
          >
            <PaperPlaneTilt size={12} className="mr-1" /> {sendingRecall ? 'Sending…' : 'Chase'}
          </Button>
          <a
            href={csvHref || '#'}
            onClick={(e) => { if (!csvHref) e.preventDefault(); }}
            target="_blank" rel="noopener noreferrer"
            data-testid="investigator-export-csv"
            className="inline-flex items-center gap-1 text-xs font-bold px-2 h-9 border border-border hover:bg-muted whitespace-nowrap"
          >
            <Download size={12} /> CSV
          </a>
        </div>
      </div>

      {/* Balance summary tiles */}
      {ledger && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <SummaryTile label="Opening" value={ledger.opening_balance} testId="inv-tile-opening" />
          <SummaryTile label="Closing" value={ledger.closing_balance} testId="inv-tile-closing" />
          <SummaryTile
            label="Net change"
            value={ledger.net_change > 0 ? `+${ledger.net_change}` : ledger.net_change}
            tone={ledger.net_change < 0 ? 'rose' : 'emerald'}
            testId="inv-tile-net"
          />
          <SummaryTile
            label="Still out (unreturned)"
            value={unret?.grand_total_missing ?? '—'}
            tone={unret?.grand_total_missing > 0 ? 'rose' : 'emerald'}
            testId="inv-tile-still-out"
          />
          <SummaryTile
            label={`Over ${thresholdDays}d threshold`}
            value={unret?.grand_total_over_threshold ?? '—'}
            tone={unret?.grand_total_over_threshold > 0 ? 'rose' : 'muted'}
            testId="inv-tile-over-threshold"
          />
        </div>
      )}

      {ledger?.first_drift_at && (
        <div className="border-l-4 border-rose-500 bg-rose-50 p-3 text-xs" data-testid="inv-drift-banner">
          <div className="flex items-center gap-2 font-bold text-rose-800">
            <WarningCircle size={14} weight="fill" /> First drift detected
          </div>
          <div className="text-rose-800/90 mt-0.5">
            The running balance first dropped below opening at <span className="font-mono">{fmtDateTime(ledger.first_drift_at)}</span> — start your trace from here.
          </div>
        </div>
      )}

      {/* Magic-link funnel analytics — chase → click → return over last 30 days */}
      {magicStats && magicStats.emails_sent + magicStats.clicks + magicStats.returns > 0 && (
        <section className="border border-border bg-card p-3" data-testid="inv-magic-link-stats">
          <div className="flex items-center gap-2 mb-2">
            <EnvelopeSimple size={16} weight="fill" className="text-brand" />
            <h3 className="text-sm font-bold">Magic-link recall funnel</h3>
            <span className="text-[10px] text-muted-foreground font-mono">last {magicStats.window_days} days</span>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-xs">
            <FunnelTile label="Chases sent" value={magicStats.emails_sent} tone="brand" testId="ml-emails" />
            <FunnelTile label="Clicks" value={magicStats.clicks} tone="brand"
              sub={`${magicStats.click_through_rate}% CTR`} testId="ml-clicks" />
            <FunnelTile label="Returns" value={magicStats.returns} tone="emerald"
              sub={`${magicStats.return_rate}% conv · ${magicStats.returns_qty} pallets`} testId="ml-returns" />
            <FunnelTile label="Returned" value={magicStats.reasons.returned} tone="emerald"
              sub={`${magicStats.reasons_qty.returned} pallets`} testId="ml-r-returned" />
            <FunnelTile label="Damaged" value={magicStats.reasons.damaged} tone="amber"
              sub={`${magicStats.reasons_qty.damaged} pallets`} testId="ml-r-damaged" />
            <FunnelTile label="Missing" value={magicStats.reasons.missing} tone="rose"
              sub={`${magicStats.reasons_qty.missing} pallets`} testId="ml-r-missing" />
          </div>
        </section>
      )}

      {/* Running-balance trend chart */}
      {chart && (
        <section data-testid="inv-trend-chart" className="border border-border bg-card p-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold flex items-center gap-2">
              <ChartLineDown size={16} weight="fill" /> Running-balance trend
            </h3>
            <div className="text-[10px] text-muted-foreground font-mono">
              {chart.first} → {chart.last} · min {chart.minV} · max {chart.maxV}
            </div>
          </div>
          <svg viewBox={`0 0 ${chart.W} ${chart.H}`} width="100%" height={chart.H} preserveAspectRatio="none" className="block">
            {/* Opening balance guideline */}
            <line x1={chart.P} x2={chart.W - chart.P} y1={chart.zeroY} y2={chart.zeroY}
              stroke="#94a3b8" strokeWidth="1" strokeDasharray="4 4" />
            <text x={chart.W - chart.P} y={chart.zeroY - 4} fontSize="10" fill="#64748b"
              textAnchor="end" fontFamily="monospace">opening {ledger.opening_balance}</text>
            {/* Line path */}
            <path d={chart.path} fill="none" stroke="#0ea5e9" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
            {/* Filled area beneath */}
            <path
              d={`${chart.path} L ${(chart.W - chart.P).toFixed(1)} ${(chart.H - chart.P).toFixed(1)} L ${chart.P} ${(chart.H - chart.P).toFixed(1)} Z`}
              fill="#0ea5e9" fillOpacity="0.08"
            />
            {/* First drift marker */}
            {chart.driftIdx >= 0 && (
              <>
                <line
                  x1={(chart.P + chart.driftIdx * chart.xStep).toFixed(1)}
                  x2={(chart.P + chart.driftIdx * chart.xStep).toFixed(1)}
                  y1={chart.P / 2} y2={chart.H - chart.P}
                  stroke="#dc2626" strokeWidth="1" strokeDasharray="3 3"
                />
                <circle
                  cx={(chart.P + chart.driftIdx * chart.xStep).toFixed(1)}
                  cy={chart.yFor(chart.rows[chart.driftIdx].running_balance).toFixed(1)}
                  r="4" fill="#dc2626" stroke="#fff" strokeWidth="1.5"
                />
                <text x={(chart.P + chart.driftIdx * chart.xStep + 6).toFixed(1)} y={chart.P}
                  fontSize="10" fill="#dc2626" fontFamily="monospace" fontWeight="bold">
                  drift
                </text>
              </>
            )}
            {/* Photo / loss / damage event markers */}
            {chart.eventMarkers.map((m) => {
              const cx = chart.P + m.i * chart.xStep;
              const cy = chart.yFor(m.row.running_balance);
              const color = m.isLoss ? (m.row.kind === 'damage' ? '#d97706' : '#dc2626') : '#0369a1';
              const title = `${m.row.created_at?.slice(0, 16) || ''} · ${m.row.kind} · qty ${m.row.qty}${m.row.photos_count ? ` · ${m.row.photos_count} photo${m.row.photos_count > 1 ? 's' : ''}` : ''}${m.row.reference ? ` · ${m.row.reference}` : ''}`;
              return (
                <g key={m.row.id} data-testid={`inv-chart-event-${m.row.id}`}>
                  <title>{title}</title>
                  <circle cx={cx.toFixed(1)} cy={cy.toFixed(1)} r={m.isLoss ? 5 : 3.5} fill={color} stroke="#fff" strokeWidth="1.5" />
                  {m.hasPhoto && (
                    <text x={cx.toFixed(1)} y={(cy + 2).toFixed(1)} fontSize="8" textAnchor="middle" fill="#fff" fontWeight="bold">📷</text>
                  )}
                </g>
              );
            })}
            {/* End cap dot */}
            <circle
              cx={(chart.W - chart.P).toFixed(1)}
              cy={chart.yFor(chart.rows[chart.rows.length - 1].running_balance).toFixed(1)}
              r="3" fill="#0ea5e9"
            />
          </svg>
          <div className="flex items-center gap-4 mt-2 text-[10px] text-muted-foreground font-mono">
            <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full" style={{ background: '#0ea5e9' }} />Balance</span>
            <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full" style={{ background: '#dc2626' }} />Loss</span>
            <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full" style={{ background: '#d97706' }} />Damage</span>
            <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full" style={{ background: '#0369a1' }} />Has photo</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-0 border-t border-dashed" style={{ borderColor: '#94a3b8' }} />Opening</span>
          </div>
        </section>
      )}

      {/* Counterparty outstanding */}
      {counter.length > 0 && (
        <section data-testid="inv-counterparty-section">
          <h3 className="text-sm font-bold mb-2 flex items-center gap-2">
            <Users size={16} weight="fill" /> Counterparty outstanding
            <span className="text-[10px] text-muted-foreground font-mono">
              (out − in per merchant)
            </span>
          </h3>
          <div className="border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 border-b border-border">
                <tr className="text-left">
                  <th className="p-2">Merchant</th>
                  <th className="p-2 text-right">OUT sent</th>
                  <th className="p-2 text-right">IN back</th>
                  <th className="p-2 text-right">Net outstanding</th>
                  <th className="p-2 text-right">Movements</th>
                </tr>
              </thead>
              <tbody>
                {counter.map((c) => (
                  <tr key={c.merchant_id} data-testid={`inv-counterparty-row-${c.merchant_id}`}
                    className="border-b border-border last:border-b-0 hover:bg-muted/30">
                    <td className="p-2 font-semibold">{c.merchant_name}</td>
                    <td className="p-2 text-right font-mono">{c.out_total}</td>
                    <td className="p-2 text-right font-mono">{c.in_total}</td>
                    <td className={`p-2 text-right font-mono font-bold ${c.net_outstanding > 0 ? 'text-rose-700' : c.net_outstanding < 0 ? 'text-emerald-700' : ''}`}>
                      {c.net_outstanding > 0 ? `+${c.net_outstanding}` : c.net_outstanding}
                    </td>
                    <td className="p-2 text-right text-xs text-muted-foreground">{c.movements}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Unreturned outbound tickets */}
      {unret && unret.merchants.length > 0 && (
        <section data-testid="inv-unreturned-section">
          <h3 className="text-sm font-bold mb-2 flex items-center gap-2">
            <ArrowSquareOut size={16} weight="fill" /> Unreturned outbound tickets
            <span className="text-[10px] text-muted-foreground font-mono">
              (FIFO-matched · aged {thresholdDays}+ days flagged)
            </span>
          </h3>
          <div className="space-y-4">
            {unret.merchants.map((m) => (
              <div key={m.merchant_id} className="border border-border bg-card" data-testid={`inv-merchant-block-${m.merchant_id}`}>
                <div className="flex items-center justify-between bg-muted/40 border-b border-border p-2">
                  <div className="text-sm font-bold">{m.merchant_name}</div>
                  <div className="text-xs font-mono">
                    <span className="text-rose-700 font-bold">{m.unreturned_total}</span> still out
                    {m.unreturned_over_threshold > 0 && (
                      <span className="ml-2 text-amber-700">
                        · {m.unreturned_over_threshold} aged {thresholdDays}+ days
                      </span>
                    )}
                  </div>
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-muted/20 border-b border-border">
                    <tr className="text-left text-[10px] uppercase tracking-wider">
                      <th className="p-2">Sent</th>
                      <th className="p-2">Kind</th>
                      <th className="p-2 text-right">Sent qty</th>
                      <th className="p-2 text-right">Still out</th>
                      <th className="p-2 text-right">Days</th>
                      <th className="p-2">Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {m.tickets.map((t) => (
                      <tr key={t.id} className={`border-b border-border last:border-b-0 ${t.over_threshold ? 'bg-amber-50/50' : ''}`}
                        data-testid={`inv-ticket-row-${t.id}`}>
                        <td className="p-2 whitespace-nowrap">{fmtDateTime(t.created_at)}</td>
                        <td className="p-2">
                          <span className={`text-[10px] uppercase tracking-wider border px-1.5 py-0.5 ${KIND_TONE[t.kind] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                            {KIND_LABELS[t.kind] || t.kind}
                          </span>
                        </td>
                        <td className="p-2 text-right font-mono">{t.sent_qty}</td>
                        <td className="p-2 text-right font-mono text-rose-700 font-bold">{t.still_out}</td>
                        <td className={`p-2 text-right font-mono ${t.over_threshold ? 'text-amber-700 font-bold' : ''}`}>{t.days_out ?? '—'}</td>
                        <td className="p-2 text-muted-foreground">{t.reference || '—'}{t.notes && <div className="text-[10px]">{t.notes}</div>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Ledger with running balance */}
      {ledger?.rows?.length > 0 && (
        <section data-testid="inv-ledger-section">
          <h3 className="text-sm font-bold mb-2 flex items-center gap-2">
            <ChartLineDown size={16} weight="fill" /> Full ledger (running balance)
          </h3>
          <div className="border border-border bg-card overflow-x-auto max-h-[500px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/40 border-b border-border sticky top-0">
                <tr className="text-left text-[10px] uppercase tracking-wider">
                  <th className="p-2">When</th>
                  <th className="p-2">Kind</th>
                  <th className="p-2 text-right">Qty</th>
                  <th className="p-2 text-right">Δ</th>
                  <th className="p-2 text-right">Running</th>
                  <th className="p-2">Merchant</th>
                  <th className="p-2">Warehouse</th>
                  <th className="p-2">Reference</th>
                </tr>
              </thead>
              <tbody>
                {ledger.rows.map((r) => (
                  <tr key={r.id}
                    className={`border-b border-border last:border-b-0 hover:bg-muted/30 ${r.id === ledger.first_drift_row_id ? 'bg-rose-50/50' : ''}`}
                    data-testid={`inv-ledger-row-${r.id}`}
                  >
                    <td className="p-2 whitespace-nowrap">{fmtDateTime(r.created_at)}</td>
                    <td className="p-2">
                      <span className={`text-[10px] uppercase tracking-wider border px-1.5 py-0.5 ${KIND_TONE[r.kind] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                        {KIND_LABELS[r.kind] || r.kind}
                      </span>
                    </td>
                    <td className="p-2 text-right font-mono">{r.qty}</td>
                    <td className={`p-2 text-right font-mono ${r.signed_qty > 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {r.signed_qty > 0 ? `+${r.signed_qty}` : r.signed_qty}
                    </td>
                    <td className="p-2 text-right font-mono font-bold">{r.running_balance}</td>
                    <td className="p-2">{r.merchant_name || merchants[r.merchant_id] || '—'}</td>
                    <td className="p-2">{r.warehouse_name || warehouses[r.warehouse_id] || '—'}</td>
                    <td className="p-2 text-muted-foreground">{r.reference || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {!loading && ledger && ledger.rows.length === 0 && (
        <div className="border border-border bg-muted/20 p-6 text-center text-sm text-muted-foreground">
          No movements found in the selected window for this pallet type.
        </div>
      )}
    </div>
  );
}

function SummaryTile({ label, value, tone, testId }) {
  const toneClass = {
    rose: 'border-rose-200 bg-rose-50 text-rose-800',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    muted: 'border-border bg-muted/30 text-muted-foreground',
  }[tone] || 'border-border bg-card text-foreground';
  return (
    <div data-testid={testId} className={`border p-3 ${toneClass}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">{label}</div>
      <div className="text-2xl font-bold font-mono mt-1">{value ?? '—'}</div>
    </div>
  );
}

function FunnelTile({ label, value, tone, sub, testId }) {
  const toneClass = {
    brand: 'border-sky-200 bg-sky-50 text-sky-900',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    amber: 'border-amber-200 bg-amber-50 text-amber-900',
    rose: 'border-rose-200 bg-rose-50 text-rose-900',
  }[tone] || 'border-border bg-card text-foreground';
  return (
    <div data-testid={testId} className={`border p-2 ${toneClass}`}>
      <div className="text-[9px] uppercase tracking-wider font-bold opacity-70">{label}</div>
      <div className="text-lg font-bold font-mono mt-0.5">{value ?? '—'}</div>
      {sub && <div className="text-[9px] opacity-70 font-mono">{sub}</div>}
    </div>
  );
}
